import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import client from './api'
import { User, LoginResult, MfaSetupData } from '../types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  verifyMfa: (challengeToken: string, code: string) => Promise<LoginResult>
  forcePasswordChange: (changeToken: string, newPassword: string) => Promise<void>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  setupMfa: () => Promise<MfaSetupData>
  enableMfa: (password: string, code: string) => Promise<void>
  disableMfa: (password: string, code: string) => Promise<void>
  refreshMe: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface LoginApiData {
  user?: User
  requires_mfa?: true
  challenge_token?: string
  challenge_expires_at?: string
  requires_password_change?: true
  change_token?: string
  change_expires_at?: string
}

/**
 * Parsed das Backend-Login-Response-Body in die typsichere LoginResult-Union.
 * Reused von login + verifyMfa + forcePasswordChange.
 */
function parseLoginData(data: LoginApiData): LoginResult {
  if (data.user) {
    return { kind: 'session', user: data.user }
  }
  if (data.requires_mfa && data.challenge_token && data.challenge_expires_at) {
    return {
      kind: 'mfa_required',
      challengeToken: data.challenge_token,
      challengeExpiresAt: data.challenge_expires_at,
    }
  }
  if (data.requires_password_change && data.change_token && data.change_expires_at) {
    return {
      kind: 'password_change_required',
      changeToken: data.change_token,
      changeExpiresAt: data.change_expires_at,
    }
  }
  throw new Error('Unexpected login response shape')
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const refreshMe = useCallback(async () => {
    try {
      const response = await client.get<{ success: boolean; data?: User }>('/auth/me')
      if (response.data.success && response.data.data) {
        setUser(response.data.data)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      await refreshMe()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [refreshMe])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const response = await client.post<{
        success: boolean
        error?: string
        data?: LoginApiData
      }>('/auth/login', { email, password })
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error ?? 'Login failed')
      }
      const result = parseLoginData(response.data.data)
      if (result.kind === 'session') {
        setUser(result.user)
      }
      return result
    },
    []
  )

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string): Promise<LoginResult> => {
      const response = await client.post<{
        success: boolean
        error?: string
        data?: LoginApiData
      }>('/auth/mfa/verify', { challenge_token: challengeToken, code })
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error ?? 'MFA verification failed')
      }
      const result = parseLoginData(response.data.data)
      if (result.kind === 'session') {
        setUser(result.user)
      }
      return result
    },
    []
  )

  const forcePasswordChange = useCallback(
    async (changeToken: string, newPassword: string): Promise<void> => {
      const response = await client.post<{
        success: boolean
        error?: string
        data?: { user: User }
      }>('/auth/change-password-forced', {
        changeToken,
        newPassword,
      })
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error ?? 'Password change failed')
      }
      setUser(response.data.data.user)
    },
    []
  )

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const response = await client.post<{ success: boolean; error?: string }>(
        '/auth/change-password',
        { currentPassword, newPassword }
      )
      if (!response.data.success) {
        throw new Error(response.data.error ?? 'Password change failed')
      }
    },
    []
  )

  const setupMfa = useCallback(async (): Promise<MfaSetupData> => {
    const response = await client.post<{
      success: boolean
      error?: string
      data?: {
        otpauth_url: string
        qr_code_data_url: string
        backup_codes: string[]
      }
    }>('/auth/mfa/setup', {})
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error ?? 'MFA setup failed')
    }
    return {
      otpAuthUrl: response.data.data.otpauth_url,
      qrCodeDataUrl: response.data.data.qr_code_data_url,
      backupCodes: response.data.data.backup_codes,
    }
  }, [])

  const enableMfa = useCallback(
    async (password: string, code: string): Promise<void> => {
      const response = await client.post<{ success: boolean; error?: string }>(
        '/auth/mfa/enable',
        { password, code }
      )
      if (!response.data.success) {
        throw new Error(response.data.error ?? 'MFA enable failed')
      }
      await refreshMe()
    },
    [refreshMe]
  )

  const disableMfa = useCallback(
    async (password: string, code: string): Promise<void> => {
      const response = await client.post<{ success: boolean; error?: string }>(
        '/auth/mfa/disable',
        { password, code }
      )
      if (!response.data.success) {
        throw new Error(response.data.error ?? 'MFA disable failed')
      }
      await refreshMe()
    },
    [refreshMe]
  )

  const logout = useCallback(async () => {
    try {
      await client.post('/auth/logout')
    } finally {
      setUser(null)
      navigate('/login')
    }
  }, [navigate])

  const value: AuthContextValue = {
    user,
    loading,
    login,
    verifyMfa,
    forcePasswordChange,
    logout,
    changePassword,
    setupMfa,
    enableMfa,
    disableMfa,
    refreshMe,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuthContext must be used inside <AuthProvider>')
  }
  return ctx
}
